<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild
            :settings="fields.settings"
            :container-page="fields.containerPage"
            :show-background-image="false"
          />
        </div>

        <div class="mb-4">
          <label class="label-input">Primary Color</label>
          <div class="group-item">
            <ColorPicker
              v-model="fields.primaryColor"
              name="sofyre-timeline-primary"
              label="Primary Color"
              default-color="#23372B"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Header</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex align-center mb-2" style="gap: 10px">
            <label class="label-input mb-0">Image</label>
            <input type="checkbox" v-model="fields.image.show" />
          </div>
          <div class="group-item">
            <v-select
              v-model="fields.image.position"
              :items="imagePositionOptions"
              item-text="text"
              item-value="value"
              label="Desktop Position"
            />
            <UploadImage
              :image="{ imageSrc: fields.image.src }"
              :height="`220`"
              :typeUpload="'image'"
              @callback="handleUploadImage"
              @callbackDelete="handleRemoveImage"
            />
            <div class="text-caption mt-2">On mobile, the image is always displayed above the tabs.</div>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Tab Helper Text</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <UploadImage
                  :image="{ imageSrc: fields.guide.image }"
                  :height="`90`"
                  :typeUpload="'image'"
                  @callback="handleUploadGuideImage"
                  @callbackDelete="handleRemoveGuideImage"
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field v-model="fields.guide.text" label="Helper Text" hide-details />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Timeline Tabs</label>
            <v-btn small text color="primary" @click="handleAddTab">
              <v-icon size="16">mdi-plus</v-icon>
              Add Tab
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(tab, tabIndex) in fields.tabs" :key="tab.id || `tab-${tabIndex}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Tab {{ tabIndex + 1 }}: {{ tab.label || 'Untitled' }}</span>
                    <v-btn icon :disabled="fields.tabs.length <= 1" @click.stop="handleRemoveTab(tabIndex)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="tab.label" label="Tab Header" />
                  <v-text-field v-model="tab.tag" label="Timeline Tag" />

                  <label class="field-label">Timeline Title</label>
                  <ckeditor :editor="editor" v-model="tab.title" :config="editorConfig" />

                  <div class="d-flex justify-space-between align-center mt-5 mb-2">
                    <label class="field-label mb-0">Features</label>
                    <v-btn small text color="primary" @click="handleAddFeature(tabIndex)">
                      <v-icon size="16">mdi-plus</v-icon>
                      Add Feature
                    </v-btn>
                  </div>
                  <v-expansion-panels accordion>
                    <v-expansion-panel
                      v-for="(feature, featureIndex) in tab.features"
                      :key="`feature-${tabIndex}-${featureIndex}`"
                    >
                      <v-expansion-panel-header>
                        <div class="d-flex justify-space-between align-center">
                          <span>Feature {{ featureIndex + 1 }}</span>
                          <v-btn icon @click.stop="handleRemoveFeature(tabIndex, featureIndex)">
                            <v-icon size="17" color="#ff6c6c">mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <label class="field-label">Feature Title</label>
                        <ckeditor :editor="editor" v-model="feature.title" :config="editorConfig" />
                        <label class="field-label mt-4">Feature Content</label>
                        <ckeditor :editor="editor" v-model="feature.content" :config="editorConfig" />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-4">
          <ButtonPrimary :button="fields.button" />
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import ButtonPrimary from './common/button-primary.vue';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const DEFAULT_FEATURE = {
  title: '<p><span style="color:#9B4600;">Feature title</span></p>',
  content: '<p><span><i>Feature content</i></span></p>',
};

const DEFAULT_BUTTON = {
  show: true,
  text: 'ADD TO CART',
  link: '#',
  backgroundColor: '#CCF46F',
  color: '#23372B',
  radius: 999,
  iconPrefix: { show: false, src: '' },
  iconSuffix: {
    show: true,
    src: '',
  },
  subBtn: { show: false, text: '' },
};

const clone = value => JSON.parse(JSON.stringify(value));

export default {
  name: 'SofyreTimelineSectionEditor',
  components: { ButtonPrimary, ColorPicker, SectionSettingsContainerFuild, UploadImage },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      imagePositionOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Right', value: 'right' },
      ],
      fields: {
        title: 'Sofyre timeline',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#F1F3E9',
          textColor: '#23372B',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
        primaryColor: '#23372B',
        header: '',
        image: { show: true, position: 'left', src: '' },
        guide: {
          image: '',
          text: '',
        },
        tabs: [],
        button: clone(DEFAULT_BUTTON),
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(value) {
        if (value && value.sectionExtract) this.applyExtract(value.sectionExtract);
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      const extractedButton = extract.button || {};
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        image: { ...this.fields.image, ...(extract.image || {}) },
        guide: { ...this.fields.guide, ...(extract.guide || {}) },
        tabs: Array.isArray(extract.tabs) ? clone(extract.tabs) : [],
        button: {
          ...this.fields.button,
          ...extractedButton,
          iconPrefix: { ...this.fields.button.iconPrefix, ...(extractedButton.iconPrefix || {}) },
          iconSuffix: { ...this.fields.button.iconSuffix, ...(extractedButton.iconSuffix || {}) },
          subBtn: { ...this.fields.button.subBtn, ...(extractedButton.subBtn || {}) },
        },
        guarantee: { ...this.fields.guarantee, ...(extract.guarantee || {}) },
      };
    },
    handleAddTab() {
      const index = this.fields.tabs.length + 1;
      this.fields.tabs.push({
        id: `sofyre-tab-${index}`,
        label: `WEEK ${index}`,
        tag: `WEEK ${index}`,
        title: '<h3>Timeline title</h3>',
        features: [clone(DEFAULT_FEATURE)],
      });
    },
    handleRemoveTab(index) {
      if (this.fields.tabs.length <= 1) return;
      this.fields.tabs.splice(index, 1);
      this.fields.tabs.forEach((tab, tabIndex) => this.$set(tab, 'id', `sofyre-tab-${tabIndex + 1}`));
    },
    handleAddFeature(tabIndex) {
      const tab = this.fields.tabs[tabIndex];
      if (!tab.features) this.$set(tab, 'features', []);
      tab.features.push(clone(DEFAULT_FEATURE));
    },
    handleRemoveFeature(tabIndex, featureIndex) {
      this.fields.tabs[tabIndex].features.splice(featureIndex, 1);
    },
    handleUploadImage(obj) {
      this.fields.image.src = (obj && obj.imageSrc) || '';
    },
    handleRemoveImage() {
      this.fields.image.src = '';
    },
    handleUploadGuideImage(obj) {
      this.fields.guide.image = (obj && obj.imageSrc) || '';
    },
    handleRemoveGuideImage() {
      this.fields.guide.image = '';
    },
    handleUploadGuaranteeImage(obj) {
      this.fields.guarantee.image = (obj && obj.imageSrc) || '';
    },
    handleRemoveGuaranteeImage() {
      this.fields.guarantee.image = '';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionSofyreTimeline(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html,
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.label-input {
  display: block;
  margin-bottom: 10px;
  color: #000;
  font-size: 18px !important;
  font-weight: 500;
}
.field-label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}
.group-item {
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
.header-modal,
.footer-modal {
  position: sticky;
  left: 0;
  z-index: 999;
  padding: 0 16px;
  background: #fff;
  box-shadow: 0 0 12px #0000000f;
}
.header-modal {
  top: 0;
}
.footer-modal {
  bottom: 0;
}
</style>
